import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StructureComponent } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const peopleRatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/rates',
  component: PeopleRatesPage,
});

function formatRupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// Rows are edited in human units (percent / rupees) and converted to the
// integer basis-points / paise the API stores.
type ComponentRow = { name: string; basis: StructureComponent['basis']; amount: string };

const DEFAULT_ROWS: ComponentRow[] = [
  { name: 'Basic', basis: 'percent_of_gross_bpp', amount: '50' },
  { name: 'HRA', basis: 'percent_of_gross_bpp', amount: '40' },
];

function rowsFromComponents(components: StructureComponent[]): ComponentRow[] {
  return components.map((c) => ({
    name: c.name,
    basis: c.basis,
    amount: String(c.value / 100),
  }));
}

function componentsFromRows(rows: ComponentRow[]): StructureComponent[] {
  return rows
    .filter((r) => r.name.trim() !== '' && r.amount.trim() !== '')
    .map((r) => ({
      name: r.name.trim(),
      basis: r.basis,
      value: Math.round(parseFloat(r.amount) * 100),
    }));
}

function describeComponents(components: StructureComponent[]): string {
  return components
    .map((c) =>
      c.basis === 'percent_of_gross_bpp'
        ? `${c.name} ${c.value / 100}%`
        : `${c.name} ₹${(c.value / 100).toLocaleString('en-IN')}`,
    )
    .join(' · ');
}

function ComponentsEditor({
  rows,
  onChange,
}: {
  rows: ComponentRow[];
  onChange: (rows: ComponentRow[]) => void;
}): JSX.Element {
  const update = (i: number, patch: Partial<ComponentRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr_11rem_7rem_2rem] gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <span>Component</span>
        <span>Basis</span>
        <span>Value</span>
        <span />
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_11rem_7rem_2rem] items-center gap-2">
          <Input value={r.name} onChange={(e) => update(i, { name: e.currentTarget.value })} />
          <Select
            value={r.basis}
            onChange={(e) =>
              update(i, { basis: e.currentTarget.value as StructureComponent['basis'] })
            }
          >
            <option value="percent_of_gross_bpp">% of gross</option>
            <option value="flat_paise">Flat ₹ / month</option>
          </Select>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={r.amount}
            onChange={(e) => update(i, { amount: e.currentTarget.value })}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={rows.length <= 1}
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
          >
            ×
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, { name: '', basis: 'percent_of_gross_bpp', amount: '' }])}
        >
          Add component
        </Button>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        Residue after declared percents rolls to Special Allowance automatically.
      </p>
    </div>
  );
}

function PeopleRatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const [rateOpen, setRateOpen] = React.useState(false);
  const [ratePerson, setRatePerson] = React.useState('');
  const [rateAmount, setRateAmount] = React.useState('');
  const [rateValidFrom, setRateValidFrom] = React.useState('');

  const [structOpen, setStructOpen] = React.useState(false);
  const [structPerson, setStructPerson] = React.useState('');
  const [structValidFrom, setStructValidFrom] = React.useState('');
  const [structRows, setStructRows] = React.useState<ComponentRow[]>(DEFAULT_ROWS);
  const [structTemplateId, setStructTemplateId] = React.useState('');

  const [templateOpen, setTemplateOpen] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [templateRows, setTemplateRows] = React.useState<ComponentRow[]>(DEFAULT_ROWS);

  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });
  const ratesQuery = useQuery({
    queryKey: ['person-rates'],
    queryFn: () => api.listPersonRates(),
  });
  const structuresQuery = useQuery({
    queryKey: ['salary-structures'],
    queryFn: () => api.listSalaryStructures(),
  });
  const templatesQuery = useQuery({
    queryKey: ['component-templates'],
    queryFn: () => api.listComponentTemplates(),
  });

  const createRate = useMutation({
    mutationFn: () =>
      api.createPersonRate({
        personId: ratePerson,
        monthlyCostPaise: Math.round(parseFloat(rateAmount) * 100),
        validFrom: rateValidFrom,
      }),
    onSuccess: async () => {
      setRateOpen(false);
      setRateAmount('');
      setRateValidFrom('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['person-rates'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const createStructure = useMutation({
    mutationFn: () =>
      api.createSalaryStructure({
        personId: structPerson,
        validFrom: structValidFrom,
        components: componentsFromRows(structRows),
      }),
    onSuccess: async () => {
      setStructOpen(false);
      setStructValidFrom('');
      setStructRows(DEFAULT_ROWS);
      setStructTemplateId('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['salary-structures'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const createTemplate = useMutation({
    mutationFn: () =>
      api.createComponentTemplate({
        name: templateName.trim(),
        components: componentsFromRows(templateRows),
      }),
    onSuccess: async () => {
      setTemplateOpen(false);
      setTemplateName('');
      setTemplateRows(DEFAULT_ROWS);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['component-templates'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const people = peopleQuery.data ?? [];
  const rates = ratesQuery.data ?? [];
  const structures = structuresQuery.data ?? [];
  const templates = templatesQuery.data ?? [];
  const structComponentsValid = componentsFromRows(structRows).length > 0;
  const templateComponentsValid = componentsFromRows(templateRows).length > 0;

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          People · Rates & structures
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Effective-dated monthly CTC and salary component splits feed payroll.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Person rates</CardTitle>
            <CardDescription>{rates.length} row(s), all history</CardDescription>
          </div>
          <Dialog open={rateOpen} onOpenChange={setRateOpen}>
            <DialogTrigger asChild>
              <Button>Add rate</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add person rate</DialogTitle>
                <DialogDescription>
                  Effective-dated. Payroll picks the newest row with validFrom ≤ month end.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Employee</Label>
                  <Select
                    value={ratePerson}
                    onChange={(e) => setRatePerson(e.currentTarget.value)}
                  >
                    <option value="">— select —</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.email})
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Monthly cost (INR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={rateAmount}
                    onChange={(e) => setRateAmount(e.currentTarget.value)}
                    placeholder="e.g. 100000"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Valid from</Label>
                  <Input
                    type="date"
                    value={rateValidFrom}
                    onChange={(e) => setRateValidFrom(e.currentTarget.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => createRate.mutate()}
                  disabled={
                    ratePerson === '' ||
                    rateAmount.trim() === '' ||
                    rateValidFrom === '' ||
                    createRate.isPending
                  }
                >
                  {createRate.isPending ? 'Adding…' : 'Add'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {rates.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No person rates yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rates.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">{r.personName ?? r.personId}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      From {r.validFrom}
                    </div>
                  </div>
                  <div className="font-semibold">₹ {formatRupees(r.monthlyCostPaise)} / mo</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Salary structures</CardTitle>
            <CardDescription>
              {structures.length} row(s). Residue after declared percents rolls to Special Allowance.
            </CardDescription>
          </div>
          <Dialog open={structOpen} onOpenChange={setStructOpen}>
            <DialogTrigger asChild>
              <Button>Add structure</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add salary structure</DialogTitle>
                <DialogDescription>
                  Start from a template or build the split by hand.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>Employee</Label>
                    <Select
                      value={structPerson}
                      onChange={(e) => setStructPerson(e.currentTarget.value)}
                    >
                      <option value="">— select —</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.email})
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Valid from</Label>
                    <Input
                      type="date"
                      value={structValidFrom}
                      onChange={(e) => setStructValidFrom(e.currentTarget.value)}
                    />
                  </div>
                  <div className="col-span-2 flex flex-col gap-1.5">
                    <Label>Apply template</Label>
                    <Select
                      value={structTemplateId}
                      onChange={(e) => {
                        const id = e.currentTarget.value;
                        setStructTemplateId(id);
                        const t = templates.find((x) => x.id === id);
                        if (t) setStructRows(rowsFromComponents(t.components));
                      }}
                    >
                      <option value="">— none —</option>
                      {templates
                        .filter((t) => t.active)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>
                <ComponentsEditor rows={structRows} onChange={setStructRows} />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => createStructure.mutate()}
                  disabled={
                    structPerson === '' ||
                    structValidFrom === '' ||
                    !structComponentsValid ||
                    createStructure.isPending
                  }
                >
                  {createStructure.isPending ? 'Adding…' : 'Add'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {structures.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No structures yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {structures.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">{s.personName ?? s.personId}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      From {s.validFrom} · {describeComponents(s.components)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Component templates</CardTitle>
            <CardDescription>
              Reusable splits (e.g. “Standard 50/40”) to apply when adding a structure.
            </CardDescription>
          </div>
          <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">New template</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>New component template</DialogTitle>
                <DialogDescription>Name it after the grade or band it applies to.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Name</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.currentTarget.value)}
                    placeholder="e.g. Standard 50/40"
                  />
                </div>
                <ComponentsEditor rows={templateRows} onChange={setTemplateRows} />
              </div>
              <DialogFooter>
                <Button
                  onClick={() => createTemplate.mutate()}
                  disabled={
                    templateName.trim() === '' || !templateComponentsValid || createTemplate.isPending
                  }
                >
                  {createTemplate.isPending ? 'Saving…' : 'Save template'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No templates yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {templates.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {describeComponents(t.components)}
                    </div>
                  </div>
                  {!t.active ? <Badge variant="muted">Inactive</Badge> : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
